import { OnboardingTooltip, Outside } from '@cian/ui-kit';
import { ITelemetryLogger } from '@cian/telemetry/shared';
import { IResultSuggestions, IItemClickParams, ESuggestType, GeoSuggest, EInputType } from '@cian/geosuggest-widget';
import { mergeStyles } from '@cian/utils';
import debounce from 'lodash.debounce';
import React, { useCallback, useEffect, useMemo, useState, FC } from 'react';
import { ISuggestionsState, ESuggestionsStatus } from '../../types/filters';
import { TSuggestResult, IYandexSuggestOptions } from '../../actions/filters';
import stylesCommon from '../FiltersPanel/FiltersPanel.css';
import styles from './FilterGeo.css';
import { IGeoBoundedBy, IGeoValue, IJsonQueryGeo } from '../../types/filters/user_geo';
import cross from './assets/cross.svg';
import { ISuggestGeoItem, normalizeGeoObject } from './helpers';
import { BOUNDED_BY_DEFAULT, MIN_QUERY_LENGTH } from '../../constants/filters/geo';
import { useApplicationContext } from '../../utils/applicationContext';

export interface IFilterGeoProps {
  suggestions: ISuggestionsState;
  geo: IJsonQueryGeo;
  regionOrMaintownId: number;
  isLoading: boolean;
  search: string;
  geoError: string | null;
  telemetry: ITelemetryLogger;

  getGeoBbox(regionOrMaintownId: number): Promise<IGeoBoundedBy>;
  getSuggestions(query: string, options?: IYandexSuggestOptions): Promise<TSuggestResult>;
  setRegion(region: number[]): void;
  setVillage(villageId: number | undefined): void;
  setBSCenters(bsCenterIds: number[]): void;
  selectGeo(geoValue: IGeoValue): void;
  geocode(query: string): Promise<IGeoValue>;
  clearGeo(): void;
  setBuilder(builderId: number): void;
  setCoworkingIds: (coworkingIds: number[]) => void;
  changeSearch: (search: string) => void;
  clearGeoError: () => void;
}

export interface IFilterGeoState {
  active: boolean;
  value: string;
  geoBoundedBy: [[number, number], [number, number]];
}

export const GEO_OBJECT_TYPES_MAP = {
  [ESuggestType.city]: 'location',
  [ESuggestType.district]: 'district',
  [ESuggestType.newbuilding]: 'newobject',
  [ESuggestType.railway]: 'railway',
  [ESuggestType.road]: 'highway',
  [ESuggestType.underground]: 'underground',
  [ESuggestType.village]: 'village',
  [ESuggestType.regionalDistrict]: 'regional_district_id',
};

export const FilterGeo: FC<IFilterGeoProps> = ({
  regionOrMaintownId,
  suggestions: { suggestions, sortOrder, status },
  geo,
  isLoading,
  search,
  geoError,
  clearGeoError,
  getGeoBbox,
  getSuggestions: getSuggestionsBase,
  clearGeo,
  geocode,
  setVillage,
  setRegion,
  setBuilder,
  selectGeo,
  setBSCenters,
  setCoworkingIds,
  changeSearch,
  telemetry,
}) => {
  const ctx = useApplicationContext();

  const [{ active, value, geoBoundedBy }, setState] = useState<IFilterGeoState>({
    active: false,
    value: '',
    geoBoundedBy: BOUNDED_BY_DEFAULT,
  });

  const getError = useCallback(() => {
    switch (status) {
      case ESuggestionsStatus.Succeed:
        if (Object.keys(suggestions).length === 0) {
          return {
            title: 'Ничего не найдено',
            text: 'Укажите другой адрес',
          };
        }
        break;

      case ESuggestionsStatus.Failed:
        return {
          title: 'Не удалось получить возможные варианты',
          text: 'Попробуйте еще раз',
        };
    }

    return undefined;
  }, [status, suggestions]);

  const getSuggestions = useCallback(
    async (query: string): Promise<void> => {
      getSuggestionsBase(query, {
        boundedBy: geoBoundedBy,
        results: 5,
      });
    },
    [geoBoundedBy, getSuggestionsBase],
  );

  const debouncedGetSuggestions = useMemo(() => debounce(getSuggestions, 300), [getSuggestions]);

  const isCleanable = (active && !!value) || (!active && !!search) || !!geo;

  const handleOutsideClick = useCallback(() => {
    setState(prevState => ({ ...prevState, active: false }));
  }, []);

  const handleClearButtonClick = useCallback(() => {
    clearGeo();

    setState(prevState => ({
      ...prevState,
      value: '',
      active: false,
    }));
  }, [clearGeo]);

  const handleValueChange = useCallback(
    (value: string) => {
      setState(prevState => ({
        ...prevState,
        value,
      }));

      if (value.length >= MIN_QUERY_LENGTH) {
        debouncedGetSuggestions(value);
      }
    },
    [debouncedGetSuggestions],
  );

  const handleInputFocus = useCallback(() => {
    setState(prevState => ({
      ...prevState,
      active: true,
    }));

    if (geoError) {
      clearGeoError();
    }

    telemetry.increment('filterGeo.input.focus');
  }, [clearGeoError, geoError, telemetry]);

  const handleItemClick = useCallback(
    async (item: IItemClickParams) => {
      switch (item.group) {
        case ESuggestType.geo:
          await geocode(item.title);
          break;

        case ESuggestType.city:
        case ESuggestType.district:
        case ESuggestType.road:
        case ESuggestType.underground:
        case ESuggestType.railway:
        case ESuggestType.newbuilding:
        case ESuggestType.regionalDistrict:
          if (item.regionId) {
            setRegion([item.regionId]);
          }
          selectGeo(normalizeGeoObject(item as ISuggestGeoItem));
          break;

        case ESuggestType.village:
          if (item.regionId) {
            setRegion([item.regionId]);
          }
          setVillage(item.id);
          break;

        case ESuggestType.businessCenter:
        case ESuggestType.shoppingCenter:
          if (item.regionId) {
            setRegion([item.regionId]);
          }

          if (item.id) {
            setBSCenters([item.id]);
          }
          break;
        case ESuggestType.builder:
          if (item.id) {
            setBuilder(item.id);
          }
          break;
        case ESuggestType.coworking:
          if (item.id) {
            setCoworkingIds([item.id]);
          }
          break;
      }

      changeSearch(item.title);

      setState(prevState => ({
        ...prevState,
        value: item.title,
        active: false,
      }));

      telemetry.increment('filterGeo.geoSuggest.click');
    },
    [changeSearch, geocode, selectGeo, setBSCenters, setBuilder, setCoworkingIds, setRegion, setVillage, telemetry],
  );

  useEffect(() => {
    const setGeoBoundedBy = async () => {
      try {
        const {
          data: {
            boundedBy: { upperCorner, lowerCorner },
          },
        } = await getGeoBbox(regionOrMaintownId);

        setState(prevState => ({
          ...prevState,
          geoBoundedBy: [
            [upperCorner.lng, upperCorner.lat],
            [lowerCorner.lng, lowerCorner.lat],
          ],
        }));
      } catch (err) {
        ctx.logger.error(err, { message: `Failed to get BBox for regionOrMaintownId = ${regionOrMaintownId}` });
      }
    };

    setGeoBoundedBy();
  }, [getGeoBbox, regionOrMaintownId, ctx.logger]);

  return (
    <Outside onOutside={handleOutsideClick}>
      <div {...mergeStyles(stylesCommon['container'], styles['container'])} data-mark="FilterGeo" data-name="FilterGeo">
        <OnboardingTooltip
          theme="blue"
          placement="bottom-start"
          open={!!geoError}
          content={<div className={styles['tooltip']}>{geoError}</div>}
          onClose={clearGeoError}
        >
          <div className={styles['tooltip-anchor']}></div>
        </OnboardingTooltip>

        <GeoSuggest
          value={value}
          suggestData={{
            suggestions: suggestions as IResultSuggestions,
            sortOrder,
          }}
          selectedValue={search}
          placeholder="Город, адрес, метро, район, ж/д или шоссе"
          error={getError()}
          inputType={EInputType.search}
          onItemClick={handleItemClick}
          onValueChange={handleValueChange}
          onFocus={handleInputFocus}
          popupStyle={styles['dropdown']}
          isLoading={isLoading}
          inputStyle={
            mergeStyles(stylesCommon['input'], styles['input'], isCleanable && styles['input--cleanable']).className
          }
        />
        {!isLoading && isCleanable && (
          <button className={styles['button']} onClick={handleClearButtonClick}>
            <img src={cross} />
          </button>
        )}
      </div>
    </Outside>
  );
};
